import { For, Show, createMemo } from 'solid-js'

import { getLocale, useI18n } from '../../i18n'
import { computeWeightAxisTicks } from '../weight/weightChartUtils'
import ChartValueAxis from './ChartValueAxis'
import { createContainerWidth } from './createContainerWidth'
import { computeLinearTrend, type TimelinePoint } from './metricViewUtils'

type TimelineLineChartProps = {
   points: TimelinePoint[]
   rangeStart: number
   rangeEnd: number
   formatValue: (value: number) => string
   /** Accessible description of what the line shows. */
   label: string
   /** Called on click / Enter / Space; when set, the chart reads as a toggle. */
   onToggle?: () => void
   /** Hint shown as the tooltip and appended to the accessible label. */
   toggleHint?: string
}

const HEIGHT = 280
const PADDING = { left: 84, right: 20, top: 16, bottom: 32 }

/**
 * Formats an epoch-ms timestamp as a short localized date for the x axis.
 *
 * @param timestamp - Epoch ms.
 * @returns The label.
 */
function formatAxisDate(timestamp: number) {
   return new Date(timestamp).toLocaleDateString(getLocale(), {
      month: 'short',
      day: 'numeric',
   })
}

/**
 * Computes the y domain for a set of points: always anchored at zero so pace and
 * speed read in absolute terms, with a little headroom above the highest value.
 *
 * @param points - The points.
 * @returns The min/max of the axis.
 */
function computeDomain(points: readonly TimelinePoint[]) {
   const max = Math.max(0, ...points.map(point => point.y))
   return { min: 0, max: max > 0 ? max * 1.1 : 1 }
}

type TimelineAxisProps = {
   width: number
   ticks: number[]
   toY: (value: number) => number
   formatValue: (value: number) => string
   rangeStart: number
   rangeEnd: number
}

/**
 * Renders the dotted value grid with labels and the start/end date labels.
 *
 * @param props - Tick values, the y mapper, formatter, and range.
 * @returns The axis elements.
 */
const TimelineAxis = (props: TimelineAxisProps) => (
   <>
      <ChartValueAxis
         ticks={props.ticks}
         toY={props.toY}
         formatValue={props.formatValue}
         left={PADDING.left}
         right={props.width - PADDING.right}
      />
      <text x={PADDING.left} y={HEIGHT - 10} font-size="12" fill="currentColor">
         {formatAxisDate(props.rangeStart)}
      </text>
      <text
         x={props.width - PADDING.right}
         y={HEIGHT - 10}
         text-anchor="end"
         font-size="12"
         fill="currentColor"
      >
         {formatAxisDate(props.rangeEnd)}
      </text>
   </>
)

type TrendLine = { x1: number; y1: number; x2: number; y2: number }

type TimelineSeriesProps = {
   width: number
   points: TimelinePoint[]
   toX: (timestamp: number) => number
   toY: (value: number) => number
   trendLine: TrendLine | null
   formatValue: (value: number) => string
}

/**
 * Renders the data line, its points, and the dashed trend — or the empty message.
 *
 * @param props - Points, pixel mappers, trend line, and formatter.
 * @returns The series elements.
 */
const TimelineSeries = (props: TimelineSeriesProps) => {
   const t = useI18n()

   return (
      <Show
         when={props.points.length > 0}
         fallback={
            <text
               x={(props.width + PADDING.left - PADDING.right) / 2}
               y={HEIGHT / 2}
               text-anchor="middle"
               font-size="13"
               fill="currentColor"
            >
               {t('No entries in this range yet.')}
            </text>
         }
      >
         <Show when={props.trendLine}>
            {line => (
               <line
                  x1={line().x1}
                  y1={line().y1}
                  x2={line().x2}
                  y2={line().y2}
                  stroke="var(--chart-target, #7dd3fc)"
                  stroke-width="1.5"
                  stroke-dasharray="4 4"
               />
            )}
         </Show>
         <polyline
            fill="none"
            stroke="var(--chart-line, #38bdf8)"
            stroke-width="2.5"
            stroke-linejoin="round"
            points={props.points
               .map(point => `${props.toX(point.x)},${props.toY(point.y)}`)
               .join(' ')}
         />
         <For each={props.points}>
            {point => (
               <circle
                  cx={props.toX(point.x)}
                  cy={props.toY(point.y)}
                  r="4"
                  fill="var(--chart-point, #f8fafc)"
               >
                  <title>{`${formatAxisDate(point.x)} · ${props.formatValue(point.y)}`}</title>
               </circle>
            )}
         </For>
      </Show>
   )
}

/**
 * Renders the primary value over time as an SVG line with a dashed linear trend.
 * Deliberately simpler than the weight chart: no mode toggle, no BMI guides.
 *
 * @param props - The points, range, and formatter.
 * @returns The chart element.
 */
const TimelineLineChart = (props: TimelineLineChartProps) => {
   const container = createContainerWidth()
   const domain = createMemo(() => computeDomain(props.points))

   /**
    * Maps a timestamp to an x pixel.
    *
    * @param timestamp - Epoch ms.
    * @returns The x coordinate.
    */
   const toX = (timestamp: number) => {
      const span = Math.max(1, props.rangeEnd - props.rangeStart)
      return (
         PADDING.left +
         ((timestamp - props.rangeStart) / span) *
            (container.width() - PADDING.left - PADDING.right)
      )
   }

   /**
    * Maps a value to a y pixel.
    *
    * @param value - The value.
    * @returns The y coordinate.
    */
   const toY = (value: number) => {
      const { min, max } = domain()
      const ratio = (value - min) / Math.max(max - min, Number.EPSILON)
      return HEIGHT - PADDING.bottom - ratio * (HEIGHT - PADDING.top - PADDING.bottom)
   }

   const ticks = createMemo(() => computeWeightAxisTicks(domain().min, domain().max))
   const trend = createMemo(() => computeLinearTrend(props.points))
   const trendLine = createMemo(() => {
      const fit = trend()
      if (!fit || props.points.length < 2) {
         return null
      }
      const first = props.points[0]!.x
      const last = props.points[props.points.length - 1]!.x
      return {
         x1: toX(first),
         y1: toY(fit.intercept + fit.slope * first),
         x2: toX(last),
         y2: toY(fit.intercept + fit.slope * last),
      }
   })

   return (
      <div
         ref={container.ref}
         class="relative w-full overflow-hidden rounded-xl border border-edge-soft bg-surface/60"
      >
         <svg
            width={container.width()}
            height={HEIGHT}
            viewBox={`0 0 ${container.width()} ${HEIGHT}`}
            class="block text-ink-muted"
            classList={{ 'cursor-pointer': Boolean(props.onToggle) }}
            role={props.onToggle ? 'button' : 'img'}
            tabIndex={props.onToggle ? 0 : undefined}
            aria-label={
               props.toggleHint ? `${props.label}. ${props.toggleHint}` : props.label
            }
            onClick={() => props.onToggle?.()}
            onKeyDown={event => {
               if (props.onToggle && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault()
                  props.onToggle()
               }
            }}
         >
            <Show when={props.toggleHint}>
               <title>{props.toggleHint}</title>
            </Show>
            <TimelineAxis
               width={container.width()}
               ticks={props.points.length > 0 ? ticks() : []}
               toY={toY}
               formatValue={props.formatValue}
               rangeStart={props.rangeStart}
               rangeEnd={props.rangeEnd}
            />
            <TimelineSeries
               width={container.width()}
               points={props.points}
               toX={toX}
               toY={toY}
               trendLine={trendLine()}
               formatValue={props.formatValue}
            />
         </svg>
      </div>
   )
}

export default TimelineLineChart
